/**
 * Session state and the two guards every screen uses.
 *
 * `can()` and `has()` here decide what to *render*. They are a convenience for
 * the user, never a control: the server checks the same permission and the
 * same entitlement on every request that depends on one. The previous build
 * made exactly this mistake in reverse — it hid menu items and left the data
 * wide open — so it is worth being explicit about which direction the trust
 * runs.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Action, Feature, Module } from '@cornerstone/education-domain'
import { api, onSessionExpired, request, retryWhileRestarting, unreachableMessage, type SessionState } from './api'
import { installAutoCapitalize } from './autoCapitalize'
import { applyPreferences, cachedPreferences, resolvePreferences, type UserPreferences } from './preferences'

interface SessionContextValue {
  /** Null once resolved and signed out; undefined while still loading. */
  session: SessionState | null | undefined
  loading: boolean
  error: string | null
  /** Does the signed-in role grant this action on this module? */
  can: (module: Module, action: Action) => boolean
  /** Has the school bought this feature? */
  has: (feature: Feature) => boolean
  /**
   * A platform operator sitting in their own tenant rather than inside a
   * school.
   *
   * The distinction decides which application they get: the operator tenant
   * holds no school, so every school screen in it is empty at best and an
   * error at worst. True only when the account can reach the platform routes
   * *and* has not switched into a customer — once inside one, they are doing
   * school work and get the school application like anybody else.
   */
  isPlatformConsole: boolean
  signIn: (session: SessionState) => void
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  /** The person's display preferences, defaults until the session says otherwise. */
  preferences: UserPreferences
  /** Applies saved preferences at once, without reloading the session. */
  setPreferences: (next: UserPreferences) => void
  /**
   * True from a sign-in on this tab until the start page has been honoured
   * once. A reload is not a sign-in: it keeps the page it was on.
   */
  freshSignIn: boolean
  clearFreshSignIn: () => void
}

/**
 * How long the sign-in form waits for the session check before showing
 * itself anyway.
 *
 * Long enough that a warm answer always wins the race — `/auth/me`
 * returns in a few hundred milliseconds against a running instance — and
 * short enough that a cold one is not something anybody sits through.
 */
const BOOT_REVEAL_MS = 1500

/**
 * The reveal's state change, kept separate so it can be tested.
 *
 * It must only act on "still waiting". If the session check has already
 * answered — signed in, or signed out — that answer stands. Writing
 * `setSession(null)` unconditionally would sign out somebody whose
 * session arrived in the moment before the timer fired, and that is a
 * race that would be rare, intermittent and horrible to track down.
 */
export const revealSignedOut = <T,>(current: T | null | undefined): T | null =>
  current === undefined ? null : current

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionState | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [preferences, setPreferencesState] = useState<UserPreferences>(() => cachedPreferences())
  const [freshSignIn, setFreshSignIn] = useState(false)

  // The session carries preferences on every load; the login response does
  // not, so after a sign-in they are fetched once.
  useEffect(() => {
    if (!session) return
    if (session.preferences) {
      setPreferencesState(resolvePreferences(session.preferences))
      return
    }
    let cancelled = false
    request<{ preferences: UserPreferences }>('/me/preferences')
      .then((result) => !cancelled && setPreferencesState(resolvePreferences(result.preferences)))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [session])

  useEffect(() => applyPreferences(preferences), [preferences])
  useEffect(() => (preferences.autoCapitalize ? installAutoCapitalize() : undefined), [preferences.autoCapitalize])

  const load = useCallback(async (signal?: AbortSignal) => {
    /*
     * The sign-in form does not wait on the session check.
     *
     * Opening the site renders a "Signing you in" skeleton until
     * `/auth/me` answers, which is right when the answer arrives in a
     * couple of hundred milliseconds. It is not right when the API has
     * gone cold: that call was measured at 30.9 seconds to return 401,
     * and the whole of it is spent looking at a skeleton that claims to
     * be signing in somebody who is not signed in and never was.
     *
     * So after a short wait the form is shown anyway. The request is not
     * cancelled — if a session does come back, late, the app swaps
     * straight into it, so somebody who really was signed in still lands
     * where they expect. The delay is comfortably longer than a warm
     * answer, so in the normal case nothing changes and nothing flashes.
     */
    const reveal = setTimeout(() => setSession(revealSignedOut), BOOT_REVEAL_MS)

    try {
      const next = await retryWhileRestarting(() => api.me(signal), { signal })
      setSession(next)
      setError(null)
    } catch (caught) {
      if (signal?.aborted) return
      // A 401 here is the normal signed-out case, not a failure to report.
      const status = (caught as { status?: number }).status
      if (status !== 401) {
        setError(unreachableMessage())
      }
      setSession(null)
    } finally {
      clearTimeout(reveal)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  // A 401 on any later request means the session ended — expired, revoked, or
  // the account was deactivated while the tab sat open. Drop to signed out
  // rather than leaving stale data on screen behind buttons that will fail.
  useEffect(() => onSessionExpired(() => setSession(null)), [])

  const signOut = useCallback(async () => {
    try {
      await api.logout()
    } finally {
      setSession(null)
    }
  }, [])

  const value = useMemo<SessionContextValue>(() => {
    const granted = session?.permissions ?? {}
    const features = new Set(session?.entitlements.features ?? [])

    return {
      session,
      loading: session === undefined,
      error,
      can: (module, action) => (granted[module] ?? []).includes(action),
      has: (feature) => features.has(feature),
      // `homeTenantId` is set only while switched into another tenant, so a
      // null one means "at home" — for an ordinary account, always.
      isPlatformConsole: (granted.tenants ?? []).includes('view') && (session?.homeTenantId ?? null) === null,
      signIn: (next) => {
        setSession(next)
        setError(null)
        setFreshSignIn(true)
      },
      signOut,
      refresh: () => load(),
      preferences,
      setPreferences: (next) => setPreferencesState(next),
      freshSignIn,
      clearFreshSignIn: () => setFreshSignIn(false),
    }
  }, [session, error, signOut, load, preferences, freshSignIn])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext)
  if (!context) throw new Error('useSession must be used inside a SessionProvider.')
  return context
}

/** The signed-in user, for screens that only render behind the auth guard. */
export function useCurrentUser() {
  const { session } = useSession()
  if (!session) throw new Error('useCurrentUser used outside an authenticated route.')
  return session.user
}
